import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { ParticipantType } from "../model/registrationEnums";
import { EmployeesFieldArray } from "../components/employees/EmployeesFieldArray";
import { optionalMobilnummerField } from "../components/employees/employeePhoneFieldConfig";
import { InteressentskabCreationParticipantEligibilityFields } from "../components/eligibility/InteressentskabCreationParticipantEligibilityFields";
import { emptyResponsibleParticipant } from "../model/registrationInitialValues";
import {
  SelectFormField,
  TextFormField,
} from "@blanket/components/formFields/FormFields";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { CompanyContactDetails } from "../components/CompanyContactDetails";
import { InvoiceDispatchFields } from "../components/InvoiceDispatchFields";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const InteressentskabSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const interessentskab = values.interessentskab;
  const nameLookup = useCompanyNameLookup({
    name: "interessentskab.name",
    cvr: "interessentskab.cvr",
    pNumber: "interessentskab.pNumber",
  });

  return (
    <>
      <FormSection
        id="section-interessentskab-info"
        number={2}
        title="Stamoplysninger om virksomheden"
        description="Stamoplysninger og forsikring."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyNameSuggestions
            items={nameLookup.data}
            isLoading={nameLookup.isLoading}
            onSelect={nameLookup.select}
            onDismiss={nameLookup.dismiss}
          >
            <TextFormField
              name="interessentskab.name"
              label="Virksomhedens navn"
              isRequired
              helperText='Er du opmærksom på kravene til navn, formål og branchekode i CVR? Et advokatselskab skal i navnet benytte den lovpligtige betegnelse afhængigt af selskabsformen, jf. retsplejelovens 124 a. Selskaber med drift skal have til formål at "udøve advokatvirksomhed" i CVR. Branchekoden skal være "691000: juridiske aktiviteter".'
              id="interessentskab-input-name"
            />
          </CompanyNameSuggestions>
          <div className="blanket-two-col">
            <TextFormField
              name="interessentskab.cvr"
              label="CVR-nr."
              id="interessentskab-input-cvr"
              isRequired
              type="number"
            />
            {nameLookup.availablePNumbers.length > 1 ? (
              <SelectFormField
                key="interessentskab-p-number-select"
                name="interessentskab.pNumber"
                label="P-nummer"
                id="interessentskab-input-p-number"
                isRequired
                placeholder="Vælg P-nummer"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
                options={nameLookup.availablePNumbers.map((p) => ({
                  value: String(p),
                  label: String(p),
                }))}
              />
            ) : (
              <TextFormField
                key="interessentskab-p-number-text"
                name="interessentskab.pNumber"
                label="P-nummer"
                id="interessentskab-input-p-number"
                isRequired
                type="number"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
              />
            )}
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="blanket-two-col">
              <TextFormField
                name="interessentskab.insuranceCompany"
                label="Navn på forsikringsselskab"
                id="interessentskab-input-insurance-company"
                type="text"
              />
              <TextFormField
                name="interessentskab.insurancePolicyNumber"
                label="Policenummer"
                id="interessentskab-input-insurance-policy-number"
                type="text"
              />
            </div>
            <div className="form-text mb-0">
              <p className="mb-2">
                Hvis du endnu ikke kender forsikringsselskabet og/eller
                policenummeret, skal dette eftersendes til Advokatsamfundet
                (forsikringer@advokatsamfundet.dk).
              </p>
              <p className="mb-0">
                Vær opmærksom på, at forsikringen skal være gældende fra
                opstartsdatoen.
              </p>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        id="section-interessentskab-contact"
        number={3}
        title="Kontaktoplysninger til virksomheden"
        description="Telefon, e-mail og fakturering."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyContactDetails
            namePrefix="interessentskab"
            idPrefix="interessentskab"
          />
          <TextFormField
            name="interessentskab.website"
            label="Virksomhedens hjemmeside"
            id="interessentskab-input-website"
            type="url"
          />
          <InvoiceDispatchFields
            namePrefix="interessentskab"
            idPrefix="interessentskab"
            invoiceTarget={interessentskab.invoiceTarget}
          />
        </div>
      </FormSection>

      <FormSection
        id="section-interessentskab-participants"
        number={4}
        title="Oplysninger om deltagere"
        description="Ansvarlige deltagere, ansatte og tilknytning til andre virksomheder."
      >
        <div className="d-flex flex-column gap-4">
          <SubGroupPanel
            className="blanket-participant-subsection"
            title="Ansvarlige deltagere (mindst to)"
            description="Deltagere, der er fysiske personer, skal være beskikkede advokater. Deltagende virksomheder skal allerede være registreret hos Advokatsamfundet, før de kan angives som interessenter."
          >
            <FieldArray name="interessentskab.responsibleParticipants">
              {({ push, remove }) => (
                <FieldGroup className="blanket-participant-list">
                  {interessentskab.responsibleParticipants.map(
                    (participant, index) => {
                      const namePrefix = `interessentskab.responsibleParticipants.${index}`;
                      const idPrefix = `interessentskab-rp-${index}`;

                      return (
                        <div
                          key={participant._rowId}
                          role="group"
                          aria-label={`Interessent ${index + 1}`}
                          className="blanket-participant-card"
                        >
                          <p className="fw-semibold mb-3">
                            Interessent {index + 1}
                          </p>
                          <div className="d-flex flex-column gap-4">
                            <div className="blanket-two-col">
                              <SelectFormField
                                name={`${namePrefix}.participantType`}
                                label="Type"
                                id={`${idPrefix}-type`}
                                isRequired
                                options={[
                                  {
                                    value: ParticipantType.Person,
                                    label: "Fysisk person",
                                  },
                                  {
                                    // Label only — the enum value is sent on to
                                    // the API and the PDF and must not change.
                                    value: ParticipantType.Company,
                                    label: "Advokatselskab",
                                  },
                                ]}
                              />
                              <TextFormField
                                name={`${namePrefix}.name`}
                                label="Navn"
                                id={`${idPrefix}-name`}
                                isRequired
                              />
                              <TextFormField
                                name={`${namePrefix}.email`}
                                label="Mail"
                                id={`${idPrefix}-email`}
                                isRequired
                                type="email"
                              />
                              <TextFormField
                                name={`${namePrefix}.cvrCpr`}
                                label="CVR/CPR"
                                id={`${idPrefix}-cvr`}
                                isRequired
                                type="number"
                              />
                              <TextFormField
                                name={`${namePrefix}.phone`}
                                label="Telefonnummer"
                                id={`${idPrefix}-phone`}
                                isRequired
                                type="tel"
                              />
                            </div>
                            <InteressentskabCreationParticipantEligibilityFields
                              namePrefix={namePrefix}
                              idPrefix={idPrefix}
                              isLawyerOrLawFirm={participant.isLawyerOrLawFirm}
                              passedPartnerExam={participant.passedPartnerExam}
                              leavesOtherOnEntry={
                                participant.leavesOtherOnEntry
                              }
                            />
                          </div>
                          <div className="mt-3 d-flex justify-content-end">
                            <TooltippedRemoveButton
                              onClick={() => remove(index)}
                              ariaLabel={`Fjern interessent ${index + 1}`}
                              disabled={
                                interessentskab.responsibleParticipants
                                  .length <= 2
                              }
                              tooltipContent="Mindst to interessenter skal angives"
                            >
                              Fjern
                            </TooltippedRemoveButton>
                          </div>
                        </div>
                      );
                    },
                  )}
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => push(emptyResponsibleParticipant())}
                    aria-label="Tilføj interessent"
                  >
                    Tilføj interessent
                  </Button>
                </FieldGroup>
              )}
            </FieldArray>
          </SubGroupPanel>
          <EmployeesFieldArray
            namePrefix="interessentskab"
            phoneField={optionalMobilnummerField}
          />
        </div>
      </FormSection>

      <FormSection
        id="section-interessentskab-dates"
        number={5}
        title="Oprettelses- og driftdato"
      >
        <div className="mb-4">
          <RegistrationRemarksField
            namePrefix="interessentskab"
            idPrefix="interessentskab"
          />
        </div>
        <div className="blanket-two-col">
          <TextFormField
            name="interessentskab.cvrCreationDate"
            label="Oprettelsesdato i CVR"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="interessentskab-input-cvr-creation-date"
            type="date"
          />
          <TextFormField
            name="interessentskab.startDate"
            label="Dato for opstart af drift"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="interessentskab-start-date"
            type="date"
          />
        </div>
        <RegistrationDateDisclosureNotice />
        <div className="blanket-two-col mt-4">
          <RegistrationContactFields
            namePrefix="interessentskab"
            idPrefix="interessentskab"
          />
        </div>
      </FormSection>
    </>
  );
};
